/**
 * @file calendly-event-type-mapping.schema.ts
 * @description Liga um MedicalProvider da Eva a uma agenda (event type) da
 * conta Calendly do parceiro (coleção 'calendlyEventTypeMappings').
 *
 * É um documento por `(partnerId, medicalProviderId, eventTypeUri)`, e não um
 * array dentro de um doc de settings: a mesma profissional costuma ter
 * "Primeira consulta" e "Retorno" como event types distintos, com durações e
 * cards de preparo diferentes.
 */

import { z } from 'zod';

import { CalendlyLocationKindEnum, PreparationCardSchema } from './scheduling-common.schema';

export const CalendlyEventTypeMappingSchema = z.object({
  id: z.string(),
  partnerId: z.string(),
  medicalProviderId: z.string(),

  // --- Referências no Calendly ---
  /** Dono do event type dentro da organização do parceiro. */
  calendlyUserUri: z.string(),
  eventTypeUri: z.string(),
  eventTypeSlug: z.string(),
  eventTypeName: z.string(),
  durationMinutes: z.number().int().positive(),
  schedulingUrl: z.string().url(),
  locationKind: CalendlyLocationKindEnum,

  /** Desligar aqui esconde a agenda no app sem perder a configuração. */
  active: z.boolean().default(true),

  // --- Copy exibida no app ---
  /**
   * `MedicalProviderSchema` não tem campo de bio/descrição, e a copy de
   * agendamento é específica do contexto (é diferente do perfil da
   * profissional). Quando nulo, o app cai para o nome do provider.
   */
  displayName: z.string().nullable().default(null),
  displayDescription: z.string().nullable().default(null),

  // --- Overrides do partner.schedulingConfig (null => usa o default) ---
  preparationCards: z.array(PreparationCardSchema).nullable().default(null),
  reminderOffsetsMinutes: z.array(z.number().int().positive()).nullable().default(null),
  minAdvanceMinutes: z.number().int().min(0).nullable().default(null),
  maxDaysAhead: z.number().int().min(1).max(31).nullable().default(null),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});
export type CalendlyEventTypeMapping = z.infer<typeof CalendlyEventTypeMappingSchema>;

/**
 * Body aceito na criação/atualização de um mapeamento pelo admin.
 *
 * `.strict()` é deliberado: rejeita campos desconhecidos em vez de gravá-los,
 * ao contrário de `PUT /api/partners/:id`, que grava `req.body` cru. `partnerId`
 * fica de fora porque é derivado do usuário autenticado, nunca do body.
 */
export const CalendlyEventTypeMappingInputSchema = CalendlyEventTypeMappingSchema.pick({
  medicalProviderId: true,
  calendlyUserUri: true,
  eventTypeUri: true,
  eventTypeSlug: true,
  eventTypeName: true,
  durationMinutes: true,
  schedulingUrl: true,
  locationKind: true,
  active: true,
  displayName: true,
  displayDescription: true,
  preparationCards: true,
  reminderOffsetsMinutes: true,
  minAdvanceMinutes: true,
  maxDaysAhead: true,
}).partial({
  active: true,
  displayName: true,
  displayDescription: true,
  preparationCards: true,
  reminderOffsetsMinutes: true,
  minAdvanceMinutes: true,
  maxDaysAhead: true,
}).strict();
export type CalendlyEventTypeMappingInput = z.infer<typeof CalendlyEventTypeMappingInputSchema>;
